/*
 * assets/js/src/zdroj-navstevy.js — zdroj návštěvy do skrytých polí poptávky.
 *
 * Proč (26. 10. 2026, audit Google Ads): účet si připisuje 1 407 „konverzí“,
 * ale CRM nerozlišuje placenou a organickou návštěvu, takže poptávku nejde
 * spárovat s kampaní. Formulář proto nese gclid / gbraid / wbraid / sznclid,
 * UTM, vstupní stránku, odkud návštěvník přišel a čas první návštěvy;
 * handler je vypíše do notifikačního e-mailu (sekce „Zdroj návštěvy“).
 *
 * SOUHLAS. `localStorage` je podle § 89 odst. 3 z. 127/2005 a EDPB 2/2023
 * totéž co cookie, a měření kampaní není „nezbytné“ pro vyřízení poptávky.
 * Ukládá se proto JEN se souhlasem kategorie Konverzní
 * (`cookies-settings-conversion`, lišta ji popisuje „měří, ze kterého
 * kanálu přišla poptávka“). Test je na PŘÍTOMNOST názvu, stejně jako GTM
 * proměnná 32 (odmítnutá kategorie se maže, nikdy nemá hodnotu `=false`).
 *   - se souhlasem: `za_src` = první návštěva (90 dní se nepřepisuje),
 *     `za_src_last` = poslední vstup z kampaně nebo cizího webu;
 *   - bez souhlasu: nic se neukládá, pole se plní jen z adresy a referreru
 *     TÉTO stránky (to není přístup k úložišti zařízení);
 *   - po odvolání souhlasu se oba klíče při dalším načtení smažou.
 * Google cookies (`_gcl_*`, `_ga`) se nečtou.
 *
 * Pole se plní při startu a znovu při focusin / submit (delegace v capture
 * fázi), takže přežije modal i obnovu formuláře po chybě a zachytí souhlas
 * udělený až během návštěvy. Bez JS zůstanou pole prázdná — na Z-A by bez JS
 * stejně neprošel štít `za_js`.
 */

const KLIC = 'za_src';
const KLIC_POSLEDNI = 'za_src_last';
const PREFIX_POLE = 'za_';
const MAX_STARI_MS = 90 * 24 * 60 * 60 * 1000;
const PARAMETRY = [
	'gclid',
	'gbraid',
	'wbraid',
	'sznclid',
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
];
const MAX_DELKA = 200;
const COOKIE_SOUHLAS = /(?:^|;\s*)cookies-settings-conversion=/;

/* Přepínač pro majitele: true = ukládat i bez souhlasu. Výchozí false. */
const BEZ_SOUHLASU = false;

function maSouhlas() {
	if (BEZ_SOUHLASU) return true;
	try {
		return COOKIE_SOUHLAS.test(document.cookie);
	} catch {
		return false;
	}
}

function orez(hodnota) {
	return String(hodnota || '').slice(0, MAX_DELKA);
}

function bezWww(host) {
	return String(host || '')
		.toLowerCase()
		.replace(/^www\./, '');
}

/* Hostname cizího webu, odkud návštěvník přišel; vlastní web = ''. */
function ciziReferrer() {
	try {
		if (!document.referrer) return '';
		const host = new URL(document.referrer).hostname;
		return bezWww(host) === bezWww(location.hostname) ? '' : orez(host);
	} catch {
		return '';
	}
}

function interniReferrer() {
	try {
		return !!document.referrer && '' === ciziReferrer();
	} catch {
		return false;
	}
}

/*
 * Vstup z TÉTO stránky. `null` = stránka není vstupem (vnitřní proklik bez
 * parametrů) — pak o původu nevíme nic a nic se nezapisuje.
 */
function vstupStranky() {
	const p = {};
	let maParametr = false;
	try {
		const qs = new URLSearchParams(location.search);
		for (const k of PARAMETRY) {
			const v = qs.get(k);
			if (v) {
				p[k] = orez(v);
				maParametr = true;
			}
		}
	} catch {
		/* starý prohlížeč bez URLSearchParams: bez parametrů */
	}
	const r = ciziReferrer();
	if (!maParametr && interniReferrer()) return null;
	return { p, l: orez(location.pathname), r, t: Date.now(), k: maParametr || '' !== r };
}

function cti(klic) {
	try {
		const raw = window.localStorage.getItem(klic);
		if (!raw) return null;
		const o = JSON.parse(raw);
		return o && 'object' === typeof o && o.t ? o : null;
	} catch {
		return null;
	}
}

function zapis(klic, o) {
	try {
		window.localStorage.setItem(klic, JSON.stringify({ p: o.p, l: o.l, r: o.r, t: o.t }));
	} catch {
		/* plné nebo zakázané úložiště: zdroj se jen neuloží */
	}
}

function smaz() {
	try {
		window.localStorage.removeItem(KLIC);
		window.localStorage.removeItem(KLIC_POSLEDNI);
	} catch {
		/* nic */
	}
}

const vstup = vstupStranky();

/*
 * Zápis se souhlasem. Volá se při startu i při každé interakci
 * s formulářem, takže souhlas udělený až během návštěvy se projeví hned.
 */
function zachyt() {
	if (!maSouhlas()) return;
	if (!vstup) return;
	const prvni = cti(KLIC);
	if (!prvni || Date.now() - prvni.t > MAX_STARI_MS) {
		zapis(KLIC, vstup);
	}
	// Poslední dotek: jen vstup z kampaně nebo cizího webu, přímý návrat
	// poslední zdroj nepřepisuje.
	if (vstup.k) {
		const posl = cti(KLIC_POSLEDNI);
		if (!posl || posl.t !== vstup.t) zapis(KLIC_POSLEDNI, vstup);
	}
}

function datum(t) {
	try {
		return new Date(t).toISOString().slice(0, 16);
	} catch {
		return '';
	}
}

/* Krátký popis posledního dotyku do jednoho pole. */
function popisDotyku(o) {
	if (!o) return '';
	const p = o.p || {};
	const casti = [];
	const utm = [p.utm_source, p.utm_medium, p.utm_campaign].filter(Boolean).join(' / ');
	if (utm) casti.push(utm);
	if (p.gclid || p.gbraid || p.wbraid) casti.push('gclid');
	if (p.sznclid) casti.push('sznclid');
	if (o.r) casti.push(o.r);
	if (o.l) casti.push(o.l);
	casti.push(datum(o.t));
	return orez(casti.join(' · '));
}

function zdrojProFormular() {
	if (maSouhlas()) {
		const prvni = cti(KLIC);
		if (prvni && Date.now() - prvni.t <= MAX_STARI_MS) {
			const posl = cti(KLIC_POSLEDNI);
			const jinyPosledni = posl && posl.t !== prvni.t ? posl : null;
			return { zdroj: prvni, rezim: 'ulozeno', posledni: jinyPosledni };
		}
	}
	if (vstup) return { zdroj: vstup, rezim: 'stranka', posledni: null };
	return { zdroj: null, rezim: maSouhlas() ? 'nezname' : 'bez-souhlasu', posledni: null };
}

function nastav(form, nazev, hodnota) {
	const pole = form.querySelector('input[name="' + PREFIX_POLE + nazev + '"]');
	if (pole) pole.value = hodnota;
}

function vyplnFormular(form) {
	if (!form || !form.querySelector || !form.querySelector('input[name="' + PREFIX_POLE + 'src_rezim"]')) {
		return false;
	}
	const { zdroj, rezim, posledni } = zdrojProFormular();
	const p = (zdroj && zdroj.p) || {};
	for (const k of PARAMETRY) nastav(form, k, p[k] || '');
	nastav(form, 'landing', zdroj ? zdroj.l : '');
	nastav(form, 'referrer', zdroj ? zdroj.r : '');
	nastav(form, 'first_seen', zdroj ? datum(zdroj.t) : '');
	nastav(form, 'src_last', popisDotyku(posledni));
	nastav(form, 'src_rezim', rezim);
	return true;
}

function priInterakci(e) {
	const t = e.target;
	if (!t) return;
	const form = t.form || ('FORM' === t.tagName ? t : null) || (t.closest ? t.closest('form') : null);
	if (!form) return;
	zachyt();
	vyplnFormular(form);
}

let navazano = false;

export function init() {
	if (!maSouhlas()) smaz();
	zachyt();
	if (!navazano) {
		navazano = true;
		document.addEventListener('focusin', priInterakci, true);
		document.addEventListener('submit', priInterakci, true);
	}
	let pocet = 0;
	for (const form of document.querySelectorAll('form')) {
		if (vyplnFormular(form)) pocet += 1;
	}
	return pocet;
}
